
import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Description as FileIcon, ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { GetPurchaseVoucherList, ExportPurchaseVoucherList } from "../../../Services/Services";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";

function GRN_PendingFor_Approval() {
  // ===== States =====
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedPO, setExpandedPO] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  // 🆕 Date Range States
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({ from: "", to: "" });

  const [filters, setFilters] = useState({
    date_range: "this_month",
    grn_no: "",
    supplier: "",

  });

  const location = useLocation();
  const groupedItems = items.reduce((acc, item) => {
    const key = item.purchase_order || "-";
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  // Calculate totals for each PO group
  const calculateGroupTotals = (group) => {
    let totalPOQty = 0;
    let totalRecQty = 0;
    let totalRemQty = 0;

    group.forEach((order) => {
      order.items.forEach((item) => {
        totalPOQty += item.quantity || 0;
        totalRecQty += item.received_qty || 0;
        totalRemQty += item.rejected_qty || 0;
      });
    });

    return { totalPOQty, totalRecQty, totalRemQty };
  };
  // ===== Date Helper Functions =====
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  const formatOnlyDate = (dateTimeString) => {
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ===== Fetch Function =====
  const fetchItems = async (pageNum = 1, reset = false) => {
    setLoading(true);
    try {
      const params = { ...filters };

      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      console.log("📤 GRN Pending API Params:", params);

      const data = await GetPurchaseVoucherList(params, pageNum);

      if (reset || pageNum === 1) {
        setItems(data.results || []);
      } else {
        setItems((prev) => [...prev, ...(data.results || [])]);
      }

      setHasMore(!!data.next);
      setPage(pageNum);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load pending GRNs");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Excel Export Handler
  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const exportFilters = { ...filters };

      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        exportFilters.from_date = customDateRange.from;
        exportFilters.to_date = customDateRange.to;
      }

      console.log("📥 Exporting GRN Pending with filters:", exportFilters);

      const blob = await ExportPurchaseVoucherList(exportFilters);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;

      // Generate filename
      const filename = `GRN_Pending_${new Date().getTime()}.xlsx`;
      link.setAttribute("download", filename);

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);

      if (error.response) {
        toast.error(`Export failed: ${error.response.status}`);
      } else if (error.request) {
        toast.error("No response from server");
      } else {
        toast.error("Failed to export Excel file");
      }
    } finally {
      setExcelLoading(false);
    }
  };

  // ===== useEffects =====
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchItems(1, true);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange]);

  useEffect(() => {
    if (location.state?.edited) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // ===== Handlers =====
  const fetchMoreData = () => {
    if (hasMore && !loading) {
      fetchItems(page + 1, false);
    }
  };

  const handleReset = () => {
    setFilters({
      date_range: "this_month",
      grn_no: "",
      supplier: "",

    });
    setCustomDateRange({ from: "", to: "" });
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setItems([]);
    setPage(1);
    setHasMore(true);
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };


  useEffect(() => {
    document.body.style.overflow = isMobile ? 'auto' : 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile]);
  return (
    <Box>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Filter Section */}
      <Box sx={{ mb: 2, mt: -5 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={3.5}>
            <TextField
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                  customDateRange.from &&
                  customDateRange.to
                  ? `${formatDateDisplay(customDateRange.from)} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* GRN No Filter */}
          <Grid item xs={12} sm={3}>
            <TextField
              label="GRN No"
              name="grn_no"
              size="small"
              value={filters.grn_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* Supplier Filter */}
          <Grid item xs={12} sm={3}>
            <TextField
              label="Supplier"
              name="supplier"
              size="small"
              value={filters.supplier}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
              fullWidth
            />
          </Grid>

          {/* Reset Button */}
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: "35px",
                whiteSpace: "nowrap",
                borderRadius: "0",
                fontSize: "0.7rem",
              }}
              fullWidth
            >
              Reset
            </Button>
          </Grid>

          {/* Excel Export Button */}
          <Grid item xs={12} sm={0.5}>
            <Tooltip
              title={excelLoading ? "Exporting..." : "Download Excel"}
              arrow
            >
              <span>
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleExcelExport}
                  disabled={excelLoading}
                  sx={{
                    borderColor: excelLoading ? "grey.400" : "success.main",
                    borderRadius: "4px",
                    height: "35px",
                    width: "35px",
                  }}
                >
                  {excelLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <FileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{ sx: { width: 350, maxHeight: 500 } }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDateRange.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Mobile/Desktop Conditional Rendering */}
      <InfiniteScroll
        dataLength={items.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={
          loading && (
            <Box sx={{ textAlign: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )
        }
        endMessage={
          <Typography
            sx={{ textAlign: "center", p: 2, color: "text.secondary" }}
          >
            {items.length === 0 ? "No pending GRNs found" : "No more data"}
          </Typography>
        }
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Collapsible Cards */
          <Box sx={{ bgcolor: "background.paper" }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CircularProgress size={40} />
              </Box>
            ) : Object.keys(groupedItems).length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No pending GRNs found
                </Typography>
              </Box>
            ) : (
              Object.entries(groupedItems).map(([poNo, orders], groupIndex) => {
                const firstOrder = orders[0];
                const totals = calculateGroupTotals(orders);
                const totalItems = orders.reduce(
                  (sum, o) => sum + o.items.length,
                  0
                );

                return (
                  <Box key={poNo}>
                    {/* PO Header Card */}
                    <Box
                      onClick={() =>
                        setExpandedPO(expandedPO === poNo ? null : poNo)
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        cursor: "pointer",
                        bgcolor: groupIndex % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:active": { bgcolor: "#e3f2fd" },
                        borderLeft: "4px solid #ff9800",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "#666",
                              minWidth: 25,
                            }}
                          >
                            #{groupIndex + 1}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              fontSize: "0.85rem",
                              flex: 1,
                            }}
                          >
                            PO: {firstOrder.purchase_order}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#666",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            GRN: {firstOrder.grn_no}
                          </span>
                          <span style={{ color: "#999" }}>•</span>
                          <span style={{ fontSize: "0.7rem" }}>
                            {totalItems} item{totalItems !== 1 ? "s" : ""}
                          </span>
                        </Typography>
                      </Box>
                      <IconButton size="small" sx={{ ml: 1 }}>
                        {expandedPO === poNo ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    {/* PO Details - Collapsible */}
                    <Collapse
                      in={expandedPO === poNo}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          bgcolor:
                            groupIndex % 2 === 0 ? "#f8f9fa" : "#fafafa",
                          borderLeft: "4px solid #ff9800",
                        }}
                      >
                        {/* PO Level Info */}
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 0.8,
                            mb: 1.5,
                            pb: 1,
                            borderBottom: "2px solid #e0e0e0",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              GRN NO
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#1976d2",
                              }}
                            >
                              {firstOrder.grn_no}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              PO NO
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#1976d2",
                              }}
                            >
                              {firstOrder.purchase_order}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              DATE
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {formatOnlyDate(firstOrder.created_at)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              TOTAL ITEMS
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "#4caf50",
                              }}
                            >
                              {totalItems}
                            </Typography>
                          </Box>

                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "#999",
                                letterSpacing: "0.5px",
                              }}
                            >
                              SUPPLIER
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: 500,
                                color: "#555",
                              }}
                            >
                              {firstOrder.supplier_name || "-"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Items List */}
                        <Typography
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            color: "#666",
                            mb: 1,
                            letterSpacing: "0.5px",
                          }}
                        >
                          ITEMS ({totalItems})
                        </Typography>

                        {orders.map((order, orderIdx) =>
                          order.items.map((item, itemIdx) => (
                            <Box
                              key={`${orderIdx}-${itemIdx}`}
                              sx={{
                                mb: 1.5,
                                p: 1,
                                bgcolor: "#fff",
                                borderRadius: "4px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: "#1976d2",
                                  mb: 0.8,
                                }}
                              >
                                Item #{orderIdx * order.items.length + itemIdx + 1}
                              </Typography>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: 0.8,
                                }}
                              >
                                {/* Item Name */}
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ITEM NAME
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 500,
                                      color: "#333",
                                    }}
                                  >
                                    {item.item_name || "-"}
                                  </Typography>
                                </Box>

                                {/* Category */}
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    CATEGORY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.category_name || "-"}
                                  </Typography>
                                </Box>

                                {/* Sub Category */}
                                {/* <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    SUB CATEGORY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.sub_category_name || "-"}
                                  </Typography>
                                </Box> */}

                                {/* Item Type */}
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    ITEM TYPE
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 500,
                                      color: "#555",
                                    }}
                                  >
                                    {item.item_type || "-"}
                                  </Typography>
                                </Box>

                                {/* PO Qty */}
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    PO QTY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      color: "#1976d2",
                                    }}
                                  >
                                    {item.quantity || 0}
                                  </Typography>
                                </Box>

                                {/* Rec Qty */}
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    REC QTY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      color: "#4caf50",
                                    }}
                                  >
                                    {item.received_qty || 0}
                                  </Typography>
                                </Box>

                                {/* Rem Qty */}
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.6rem",
                                      color: "#999",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    REM QTY
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontWeight: 600,
                                      color: "#f44336",
                                    }}
                                  >
                                    {item.rejected_qty || 0}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Collapse>
                    <Divider />
                  </Box>
                );
              })
            )}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Original Table */
          <TableContainer
            component={Paper}
            id="item-table"
            sx={{
              maxHeight: "88vh",
              overflowX: { xs: "auto", sm: "unset" },
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: "max-content !important",
                "& td, & th": {
                  borderBottom: "1px solid #ccc",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#2196f3" : "#333",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      minWidth: 40,
                      padding: "0px",
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>GRN No</TableCell>
                  <TableCell>PO No</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Category</TableCell>
                  {/* <TableCell>Sub Category</TableCell> */}
                  <TableCell align="right">Item type</TableCell>
                  <TableCell align="right">Item Name</TableCell>
                  <TableCell align="right">PO Qty</TableCell>
                  <TableCell align="right">Rec Qty</TableCell>
                  <TableCell align="right">Rem Qty</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : Object.keys(groupedItems).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                      No pending GRNs found
                    </TableCell>
                  </TableRow>
                ) : (
                  Object.entries(groupedItems).map(
                    ([poNo, orders], groupIndex) => {
                      const totals = calculateGroupTotals(orders);
                      const firstOrder = orders[0];
                      const rowspan = orders.reduce(
                        (sum, o) => sum + o.items.length,
                        0
                      );

                      return (
                        <React.Fragment key={poNo}>
                          {/* Data Rows */}
                          {orders.map((order, orderIdx) =>
                            order.items.map((item, itemIdx) => {
                              const isFirstRow = orderIdx === 0 && itemIdx === 0;

                              return (
                                <TableRow
                                  hover
                                  key={`${order.id}-${itemIdx}`}
                                  sx={{
                                    backgroundColor:
                                      (item + itemIdx) % 2 === 0
                                        ? "#fafafa"
                                        : "#ffffff",
                                    "&:hover": {
                                      backgroundColor: "#e3f2fd",
                                      "& td": {
                                        backgroundColor: "#e3f2fd !important",
                                      },
                                    },
                                    "& td": {
                                      paddingTop: "2px !important",
                                      paddingBottom: "2px !important",
                                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      color: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "white"
                                          : "inherit",
                                      backgroundColor: (theme) =>
                                        theme.palette.mode === "dark"
                                          ? "black"
                                          : "inherit",
                                    },
                                  }}
                                >
                                  {isFirstRow && (
                                    <>
                                      <TableCell
                                        rowSpan={rowspan}
                                        align="center"
                                        sx={{
                                          position: "sticky",
                                          left: 0,
                                          backgroundColor: "#ffe6e6",
                                          zIndex: 2,
                                          fontWeight: "bold",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        {groupIndex + 1}
                                      </TableCell>
                                      <TableCell
                                        rowSpan={rowspan}
                                        align="center"
                                        sx={{ verticalAlign: "middle" }}
                                      >
                                        {firstOrder.grn_no}
                                      </TableCell>
                                      <TableCell
                                        rowSpan={rowspan}
                                        align="center"
                                        sx={{ verticalAlign: "middle" }}
                                      >
                                        {firstOrder.purchase_order}
                                      </TableCell>
                                      <TableCell
                                        rowSpan={rowspan}
                                        align="center"
                                        sx={{ verticalAlign: "middle" }}
                                      >
                                        {formatOnlyDate(firstOrder.created_at)}
                                      </TableCell>
                                      <TableCell
                                        rowSpan={rowspan}
                                        align="center"
                                        sx={{ verticalAlign: "middle" }}
                                      >
                                        {firstOrder.supplier_name}
                                      </TableCell>
                                    </>
                                  )}

                                  <TableCell align="center">
                                    {item.category_name}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.sub_category_name}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.item_type}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.item_name}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.received_qty}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.rejected_qty}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </React.Fragment>
                      );
                    }
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
    </Box>
  );
}

export default GRN_PendingFor_Approval;